import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import type { CreateFieldInput } from '../schemas';

interface FieldDisplaySettingsProps {
  control: Control<CreateFieldInput>;
  isCurrency: boolean;
}

const PERMISSION_OPTION_KEYS = ['all', 'members', 'admins'] as const;
const CURRENCY_OPTION_VALUES = ['MXN', 'USD', 'EUR'] as const;

/** Permissions/currency selects + the required/pinned/visibility toggles for `FieldCreateForm`. */
export function FieldDisplaySettings({ control, isCurrency }: FieldDisplaySettingsProps) {
  const { t } = useTranslation('customFields');

  const permissionOptions = PERMISSION_OPTION_KEYS.map((key) => ({
    value: key,
    label: t(`createForm.permissionOptions.${key}`),
  }));

  const currencyOptions = CURRENCY_OPTION_VALUES.map((code) => ({
    value: code,
    label: t(`createForm.currencyOptions.${code.toLowerCase()}`),
  }));

  return (
    <>
      {isCurrency && (
        <FormFieldSelect
          control={control}
          name="currencyCode"
          label={t('createForm.currencyCode')}
          options={currencyOptions}
          floatingLabel
          data-testid="custom-fields-currency-select"
        />
      )}

      <div className="flex items-center gap-2">
        <FormFieldSelect
          control={control}
          name="typeConfig"
          label={t('createForm.permissions')}
          options={permissionOptions}
          floatingLabel
          data-testid="custom-fields-permissions-select"
        />
        <Lock className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
      </div>

      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {t('createForm.screenConfig')}
      </p>

      <Controller
        control={control}
        name="required"
        render={({ field }) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="cf-required" className="text-sm cursor-pointer">
              {t('createForm.requiredInTasks')}
            </Label>
            <Switch
              id="cf-required"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              data-testid="custom-fields-required-switch"
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="pinned"
        render={({ field }) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="cf-pinned" className="text-sm cursor-pointer">
              {t('createForm.pinned')}
            </Label>
            <Switch
              id="cf-pinned"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              data-testid="custom-fields-pinned-switch"
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="visibleToGuests"
        render={({ field }) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="cf-visibility" className="text-sm cursor-pointer leading-tight">
              {t('createForm.visibleToGuests')}
            </Label>
            <Switch
              id="cf-visibility"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              data-testid="custom-fields-visibility-switch"
            />
          </div>
        )}
      />
    </>
  );
}
